// src/pages/fleet/reports/reportKit.tsx — building blocks shared by every Fleet report:
// date-range picker, vehicle picker, report table (search · sort · paging · Excel/CSV/PDF),
// page shell and the pop-up used by the Overview cards.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { P, css } from '../fleetStyles';
import Icon, { IconName } from '../Icon';
import useIsMobile from '../useIsMobile';
import { exportReportXlsx, getVehicles } from '../../../services/fleetApi';
import { apiError } from '../../../utils/errors';
import type { Vehicle } from '../../../types';

// ── Formatting ────────────────────────────────────────────────────────────────
export const fmtDT = (v: string | null | undefined): string =>
  v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
export const fmtDur = (min: number | null | undefined): string => {
  if (min == null) return '—';
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};
export const fmtKm = (v: number | null | undefined) => (v == null ? '—' : `${v.toLocaleString('en-IN', { maximumFractionDigits: 1 })} km`);
export const mapsLink = (lat: number | null, lng: number | null) =>
  lat == null || lng == null ? null : `https://www.google.com/maps?q=${lat},${lng}`;
export const humanize = (s: string) =>
  s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\b(Gps|Sms|Api|Id|Ifms)\b/g, (w) => w.toUpperCase());

// ── Loading ───────────────────────────────────────────────────────────────────
/** Runs `load` whenever `deps` change; the newest request wins. */
export function useReport<T>(load: () => Promise<T[]>, deps: React.DependencyList) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const seq = useRef(0);
  useEffect(() => {
    const my = ++seq.current;
    setLoading(true); setError('');
    load()
      .then((r) => { if (my === seq.current) setRows(r); })
      .catch((e) => { if (my === seq.current) { setRows([]); setError(apiError(e, 'Could not load the report')); } })
      .finally(() => { if (my === seq.current) setLoading(false); });
  }, deps);
  return { rows, loading, error };
}

// ── Date range ────────────────────────────────────────────────────────────────
export interface Range { from: string; to: string }   // ISO strings
type Preset = 'today' | 'yesterday' | '7d' | '30d' | 'custom';
const PRESETS: [Preset, string][] = [['today', 'Today'], ['yesterday', 'Yesterday'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['custom', 'Custom']];

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export function presetRange(p: Exclude<Preset, 'custom'>): Range {
  const now = new Date(), today = startOfDay(now), day = 86400000;
  if (p === 'today') return { from: today.toISOString(), to: now.toISOString() };
  if (p === 'yesterday') return { from: new Date(today.getTime() - day).toISOString(), to: today.toISOString() };
  return { from: new Date(today.getTime() - (p === '7d' ? 6 : 29) * day).toISOString(), to: now.toISOString() };
}
// <input type="datetime-local"> works in local time without a zone.
const toLocal = (iso: string) => { const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const fromLocal = (v: string) => new Date(v).toISOString();

export function RangePicker({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  const [preset, setPreset] = useState<Preset>('7d');
  const pick = (p: Preset) => { setPreset(p); if (p !== 'custom') onChange(presetRange(p)); };
  return (
    <div style={S.rangeWrap}>
      <div style={S.seg}>
        {PRESETS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => pick(k)} style={{ ...S.segBtn, ...(preset === k ? S.segOn : {}) }}>{label}</button>
        ))}
      </div>
      {preset === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <input type="datetime-local" style={S.dt} value={toLocal(value.from)} max={toLocal(value.to)}
            onChange={(e) => e.target.value && onChange({ ...value, from: fromLocal(e.target.value) })} aria-label="From" />
          <span style={{ color: 'var(--fx-text-3)', fontSize: 12 }}>to</span>
          <input type="datetime-local" style={S.dt} value={toLocal(value.to)} min={toLocal(value.from)}
            onChange={(e) => e.target.value && onChange({ ...value, to: fromLocal(e.target.value) })} aria-label="To" />
        </div>
      )}
    </div>
  );
}

// ── Vehicle picker (list fetched once per page load) ─────────────────────────
let vehicleCache: Promise<Vehicle[]> | null = null;
export function VehicleSelect({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const [list, setList] = useState<Vehicle[]>([]);
  useEffect(() => {
    vehicleCache ||= getVehicles().then((r) => r.data.vehicles || []).catch(() => { vehicleCache = null; return []; });
    let live = true;
    vehicleCache.then((v) => live && setList([...v].sort((a, b) => a.vehicleNumber.localeCompare(b.vehicleNumber))));
    return () => { live = false; };
  }, []);
  return (
    <select style={S.select} value={value} onChange={(e) => onChange(e.target.value)} aria-label="Vehicle">
      <option value="">All vehicles</option>
      {list.map((v) => <option key={v.id} value={v.id}>{v.vehicleNumber}</option>)}
    </select>
  );
}

export function FilterSelect({ label, value, options, onChange }: { label: string; value: string; options: (string | { value: string; label: string })[]; onChange: (v: string) => void }) {
  return (
    <select style={S.select} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      <option value="">{label}: All</option>
      {options.map((o) => typeof o === 'string'
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ── Report table ──────────────────────────────────────────────────────────────
export interface RCol<R> {
  key: string;
  label: string;
  render?: (r: R) => React.ReactNode;
  /** Value used for searching and exports (defaults to row[key]). */
  value?: (r: R) => string | number | null | undefined;
  /** Value used for sorting when it differs from `value` (e.g. ISO date vs "22 Sept 2026"). */
  sortValue?: (r: R) => string | number | null | undefined;
  align?: 'left' | 'right';
  /** Leave out of Excel / CSV / PDF. */
  noExport?: boolean;
}

const read = <R,>(r: R, c: RCol<R>): string | number | null | undefined =>
  c.value ? c.value(r) : ((r as unknown as Record<string, string | number | null | undefined>)[c.key]);

interface TableProps<R> {
  title: string;
  /** Printed under the title in exports, e.g. the date range. */
  subtitle?: string;
  columns: RCol<R>[];
  rows: R[];
  loading?: boolean;
  error?: string;
  emptyText?: string;
  searchKeys?: string[];
  onRowClick?: (r: R) => void;
  rowKey?: (r: R) => string;
  initialSort?: { key: string; dir: 1 | -1 };
  /** Rows per page to start with (25 by default). */
  pageSize?: number;
  /** Table min-width before it scrolls sideways (0 = always fit the card). */
  minWidth?: number;
}

export function ReportTable<R>({
  title, subtitle, columns, rows, loading, error, emptyText = 'No records for this selection',
  searchKeys, onRowClick, rowKey, initialSort, pageSize = 25, minWidth,
}: TableProps<R>) {
  const isMobile = useIsMobile();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(initialSort || null);
  const [size, setSize] = useState(pageSize);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState('');
  const [exportErr, setExportErr] = useState('');

  useEffect(() => { setPage(1); }, [rows]);

  const visible = useMemo(() => {
    const n = q.trim().toLowerCase();
    const keys = searchKeys || columns.map((c) => c.key);
    let out = n
      ? rows.filter((r) => keys.some((k) => {
          const c = columns.find((x) => x.key === k);
          const v = c ? read(r, c) : (r as unknown as Record<string, unknown>)[k];
          return String(v ?? '').toLowerCase().includes(n);
        }))
      : rows;
    if (sort) {
      const c = columns.find((x) => x.key === sort.key);
      if (c) out = [...out].sort((a, b) => {
        const sv = (r: R) => (c.sortValue ? c.sortValue(r) : read(r, c));
        const x = sv(a), y = sv(b);
        if (x == null && y == null) return 0;
        if (x == null) return 1; if (y == null) return -1;
        if (typeof x === 'number' && typeof y === 'number') return (x - y) * sort.dir;
        return String(x).localeCompare(String(y), undefined, { numeric: true }) * sort.dir;
      });
    }
    return out;
  }, [rows, q, sort, columns, searchKeys]);

  const pages = Math.max(1, Math.ceil(visible.length / size));
  const cur = Math.min(page, pages);
  const slice = visible.slice((cur - 1) * size, cur * size);
  const exportCols = columns.filter((c) => !c.noExport);
  const plain = () => visible.map((r) => Object.fromEntries(exportCols.map((c) => [c.key, read(r, c) ?? ''])));
  const fileBase = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const sub = subtitle || `Generated ${new Date().toLocaleString('en-IN')}`;

  const download = (blob: Blob, name: string) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);   // revoking at once can cancel the download
  };
  const toExcel = async () => {
    setBusy('xlsx'); setExportErr('');
    try {
      const r = await exportReportXlsx({ title, subtitle: sub, columns: exportCols.map(({ key, label }) => ({ key, label })), rows: plain() });
      download(r.data, `${fileBase}.xlsx`);
    } catch (e) {
      setExportErr(apiError(e, 'Excel export failed'));
    } finally { setBusy(''); }
  };
  const toCsv = () => {
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const data = plain();
    const lines = [exportCols.map((c) => esc(c.label)).join(','), ...data.map((r) => exportCols.map((c) => esc(r[c.key])).join(','))];
    download(new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' }), `${fileBase}.csv`);
  };
  const toPdf = () => {
    const w = window.open('', '_blank');
    if (!w) { setExportErr('Allow pop-ups for this site to save as PDF'); return; }
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
    const data = plain();
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        body { font: 11px -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #1d1d1f; }
        h1 { font-size: 17px; margin: 0 0 2px; } p { margin: 0 0 12px; color: #6e6e73; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; background: #f2f2f7; font-weight: 600; }
        th, td { padding: 5px 7px; border-bottom: 1px solid #e5e5ea; vertical-align: top; }
        tr { page-break-inside: avoid; }
      </style></head><body>
      <h1>${esc(title)}</h1><p>${esc(sub)} · ${data.length} records</p>
      <table><thead><tr>${exportCols.map((c) => `<th>${esc(c.label)}</th>`).join('')}</tr></thead>
      <tbody>${data.map((r) => `<tr>${exportCols.map((c) => `<td>${esc(r[c.key])}</td>`).join('')}</tr>`).join('')}</tbody></table>
      <script>window.onload = function () { window.print(); };</script>
      </body></html>`);
    w.document.close();
  };

  const toggleSort = (k: string) => setSort((s) => (s && s.key === k ? { key: k, dir: s.dir === 1 ? -1 : 1 } : { key: k, dir: 1 }));
  const key = (r: R, i: number) => (rowKey ? rowKey(r) : String((r as unknown as { id?: string }).id ?? i));
  const disabled = loading || !visible.length;

  return (
    <div style={P.card}>
      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <span style={S.searchIcon}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg></span>
          <input style={S.search} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search in results…" />
        </div>
        <span style={S.count}>{loading ? 'Loading…' : `${visible.length.toLocaleString('en-IN')} record${visible.length === 1 ? '' : 's'}`}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" style={{ ...S.exp, ...S.expXls, opacity: disabled ? 0.5 : 1 }} disabled={disabled || !!busy} onClick={toExcel}>
            {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
          </button>
          <button type="button" style={{ ...S.exp, opacity: disabled ? 0.5 : 1 }} disabled={disabled} onClick={toCsv}>CSV</button>
          <button type="button" style={{ ...S.exp, ...S.expPdf, opacity: disabled ? 0.5 : 1 }} disabled={disabled} onClick={toPdf}>PDF</button>
        </div>
      </div>
      {exportErr && <div style={S.err}>{exportErr}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...P.table, minWidth: minWidth ?? (isMobile ? 680 : 760) }}>
          <thead>
            <tr>
              <th style={{ ...P.th, width: 44 }}>#</th>
              {columns.map((c) => (
                <th key={c.key} onClick={() => toggleSort(c.key)}
                  style={{ ...P.th, cursor: 'pointer', textAlign: c.align || 'left', userSelect: 'none' }}>
                  {c.label}{sort?.key === c.key ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={columns.length + 1} style={P.emptyTd}>Loading report…</td></tr>}
            {!loading && error && <tr><td colSpan={columns.length + 1} style={{ ...P.emptyTd, color: 'var(--fx-red)' }}>{error}</td></tr>}
            {!loading && !error && slice.map((r, i) => (
              <tr key={key(r, i)} className="fx-rrow" style={onRowClick ? P.trClick : undefined} onClick={onRowClick ? () => onRowClick(r) : undefined}>
                <td style={{ ...P.td, color: 'var(--fx-text-3)' }}>{(cur - 1) * size + i + 1}</td>
                {columns.map((c) => (
                  <td key={c.key} style={{ ...P.td, textAlign: c.align || 'left', fontVariantNumeric: c.align === 'right' ? 'tabular-nums' : undefined }}>
                    {c.render ? c.render(r) : String(read(r, c) ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && !error && slice.length === 0 && <tr><td colSpan={columns.length + 1} style={P.emptyTd}>{emptyText}</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={S.foot}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--fx-text-2)' }}>
          Rows
          <select style={{ ...S.select, minWidth: 0, padding: '4px 6px' }} value={size} onChange={(e) => { setSize(+e.target.value); setPage(1); }}>
            {[...new Set([pageSize, 25, 50, 100])].sort((a, b) => a - b).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {visible.length > 0 && <span>{(cur - 1) * size + 1}–{Math.min(cur * size, visible.length)} of {visible.length}</span>}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" style={S.pg} disabled={cur === 1} onClick={() => setPage(cur - 1)}>‹</button>
          <span style={{ ...S.pg, ...S.pgOn }}>{cur}</span>
          <span style={{ ...S.pg, cursor: 'default', color: 'var(--fx-text-3)' }}>/ {pages}</span>
          <button type="button" style={S.pg} disabled={cur === pages} onClick={() => setPage(cur + 1)}>›</button>
        </div>
      </div>
    </div>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────
export interface Stat { label: string; value: React.ReactNode; tint: string }

export function ReportShell({ icon, tint, title, subtitle, filters, stats, note, children }: {
  icon: IconName; tint: string; title: string; subtitle: string;
  filters?: React.ReactNode; stats?: Stat[]; note?: React.ReactNode; children: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  return (
    <div style={P.page}>
      <style>{`.fx-rrow:hover td { background: var(--fx-surface-2); }`}</style>
      <div style={P.head}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ ...S.titleIcon, background: `linear-gradient(180deg, ${tint}CC 0%, ${tint} 100%)` }}><Icon name={icon} size={18} stroke={2} /></div>
          <div><h1 style={P.h1}>{title}</h1><p style={P.sub}>{subtitle}</p></div>
        </div>
      </div>
      {filters && <div style={S.filters}>{filters}</div>}
      {note && <div style={S.note}>{note}</div>}
      {stats && stats.length > 0 && (
        <div style={{ ...S.stats, gridTemplateColumns: isMobile ? '1fr 1fr' : `repeat(${stats.length}, 1fr)` }}>
          {stats.map((s) => (
            <div key={s.label} style={S.stat}>
              <span style={{ ...S.statDot, background: s.tint }} />
              <div><div style={S.statVal}>{s.value}</div><div style={S.statLbl}>{s.label}</div></div>
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

// ── Pop-up ────────────────────────────────────────────────────────────────────
export function Modal({ title, subtitle, onClose, children, width, footer = true }: {
  title: string; subtitle?: string; onClose: () => void; children: React.ReactNode;
  /** Max width in px (default 1180 — report tables). */
  width?: number;
  /** Show the "press Esc / Close" footer (forms bring their own buttons). */
  footer?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <>
      <style>{`.fx-mclose:hover { background: var(--fx-red-soft) !important; color: var(--fx-red) !important; border-color: var(--fx-red) !important; }
        @keyframes fxPop { from { opacity: 0; transform: translate(-50%, -48%) scale(.97); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }`}</style>
      <div style={S.scrim} onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={title} style={{ ...S.modal, ...(width ? { width: `min(${width}px, calc(100% - 24px))` } : {}) }}>
        <div style={S.mHead}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--fx-text)', letterSpacing: '-0.01em' }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: 'var(--fx-text-2)', marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button type="button" className="fx-mclose" style={S.x} onClick={onClose} aria-label="Close" title="Close (Esc)">
            <Icon name="close" size={15} stroke={2.4} /> Close
          </button>
        </div>
        <div style={S.mBody}>{children}</div>
        {footer && (
          <div style={S.mFoot}>
            <span style={{ fontSize: 11.5, color: 'var(--fx-text-3)' }}>Tip: press Esc or click outside to close</span>
            <button type="button" style={S.mFootBtn} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </>
  );
}

// Coloured status pill.
export function Tag({ text, tone }: { text: string; tone: 'green' | 'orange' | 'red' | 'purple' | 'gray' | 'blue' }) {
  const c = { green: 'var(--fx-green)', orange: 'var(--fx-orange)', red: 'var(--fx-red)', purple: 'var(--fx-purple)', gray: 'var(--fx-text-2)', blue: 'var(--fx-accent)' }[tone];
  const bg = { green: 'var(--fx-green-soft)', orange: 'var(--fx-orange-soft)', red: 'var(--fx-red-soft)', purple: 'var(--fx-purple-soft)', gray: 'var(--fx-gray-soft)', blue: 'var(--fx-accent-soft)' }[tone];
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, color: c, background: bg, whiteSpace: 'nowrap' }}>{text}</span>;
}

export const TRIP_TONE: Record<string, 'green' | 'orange' | 'red' | 'gray' | 'blue'> = { ACTIVE: 'green', PENDING: 'orange', COMPLETED: 'blue', CANCELLED: 'red' };
export const HEALTH_TONE: Record<string, 'green' | 'orange' | 'red' | 'purple' | 'gray'> = { MOVING: 'green', HALTED: 'orange', DELAYED: 'red', GPS_GAP: 'purple', OFFLINE: 'gray' };
export const SEVERITY_TONE: Record<string, 'red' | 'orange' | 'gray' | 'purple'> = { CRITICAL: 'purple', HIGH: 'red', MEDIUM: 'orange', LOW: 'gray' };

const S = css({
  titleIcon: { width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)' },
  filters: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 12, padding: '10px 12px', marginBottom: 14, boxShadow: 'var(--fx-shadow)' },
  note: { fontSize: 12, color: 'var(--fx-text-2)', background: 'var(--fx-accent-soft)', borderRadius: 10, padding: '8px 12px', marginBottom: 14, lineHeight: 1.5 },
  rangeWrap: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  seg: { display: 'flex', background: 'var(--fx-gray-soft)', borderRadius: 9, padding: 2, gap: 2, flexWrap: 'wrap' },
  segBtn: { border: 'none', background: 'transparent', padding: '5px 10px', borderRadius: 7, fontSize: 12, fontWeight: 500, color: 'var(--fx-text-2)', cursor: 'pointer', fontFamily: 'inherit' },
  segOn: { background: 'var(--fx-surface)', color: 'var(--fx-text)', fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' },
  dt: { padding: '5px 8px', borderRadius: 8, border: '1px solid var(--fx-border-strong)', background: 'var(--fx-input)', color: 'var(--fx-text)', fontSize: 12, fontFamily: 'inherit' },
  select: { padding: '6px 9px', borderRadius: 8, border: '1px solid var(--fx-border-strong)', fontSize: 12, background: 'var(--fx-input)', color: 'var(--fx-text)', minWidth: 150, fontFamily: 'inherit' },
  toolbar: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--fx-border)' },
  searchWrap: { position: 'relative', flex: '1 1 200px', maxWidth: 300 },
  searchIcon: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--fx-text-3)', display: 'flex' },
  search: { width: '100%', padding: '7px 11px 7px 30px', borderRadius: 8, border: '1px solid transparent', background: 'var(--fx-gray-soft)', color: 'var(--fx-text)', fontSize: 12.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  count: { fontSize: 12, color: 'var(--fx-text-2)' },
  exp: { padding: '6px 12px', borderRadius: 8, border: '1px solid var(--fx-border-strong)', background: 'var(--fx-surface)', color: 'var(--fx-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  expXls: { color: '#fff', background: '#1D8348', borderColor: '#1D8348' },
  expPdf: { color: '#fff', background: '#C0392B', borderColor: '#C0392B' },
  err: { color: 'var(--fx-red)', fontSize: 12, padding: '8px 14px', background: 'var(--fx-red-soft)' },
  foot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '10px 14px' },
  pg: { minWidth: 28, height: 28, padding: '0 7px', borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--fx-text)', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  pgOn: { background: 'var(--fx-accent)', color: '#fff', fontWeight: 600, cursor: 'default' },
  stats: { display: 'grid', gap: 10, marginBottom: 14 },
  stat: { display: 'flex', alignItems: 'center', gap: 11, background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 12, padding: '12px 14px', boxShadow: 'var(--fx-shadow)' },
  statDot: { width: 4, height: 30, borderRadius: 4 },
  statVal: { fontSize: 19, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--fx-text)', lineHeight: 1.15 },
  statLbl: { fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 2 },
  scrim: { position: 'fixed', inset: 0, background: 'var(--fx-scrim)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', zIndex: 90 },
  modal: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 91, width: 'min(1180px, calc(100% - 24px))', maxHeight: 'calc(100% - 40px)', display: 'flex', flexDirection: 'column', background: 'var(--fx-bg)', border: '1px solid var(--fx-border)', borderRadius: 16, boxShadow: 'var(--fx-shadow-lg)', overflow: 'hidden', animation: 'fxPop .22s cubic-bezier(.2,.8,.2,1)' },
  mHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--fx-border)', background: 'var(--fx-surface)' },
  mBody: { padding: 16, overflow: 'auto' },
  x: { background: 'var(--fx-surface)', border: '1px solid var(--fx-border-strong)', height: 34, padding: '0 14px', borderRadius: 10, cursor: 'pointer', color: 'var(--fx-text)', display: 'flex', alignItems: 'center', gap: 6, flex: 'none', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', transition: 'background .15s, color .15s, border-color .15s' },
  mFoot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 20px', borderTop: '1px solid var(--fx-border)', background: 'var(--fx-surface)' },
  mFootBtn: { background: 'var(--fx-accent)', color: '#fff', border: 'none', padding: '8px 22px', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
});
